import PaginationComponent from '@/components/Pagination';
import CheckBox from '@/components/icon/CheckBox';
import { isEmpty } from 'lodash';
import styled, { css } from 'styled-components';
import { MouseEvent } from 'react';
import {
  MedicineStandardPriceVo,
  PharmacyHandlingMedicineList
} from '@/@types/medicine';
import { formatComma } from '@/utils/form';
import { MEDICINE_PAGE_LIMIT } from '@/hooks/medicine/useMedicineListViewModel';

const HEADERS = [
  { name: '보험코드', flex: 1 },
  { name: '약품명', flex: 2 },
  { name: '처방 단위', flex: 1 },
  { name: '금액', flex: 1 },
  { name: '품절 처리', flex: 1 }
];

interface MedicineTableProps {
  medicines: PharmacyHandlingMedicineList;
  onChangeSoldOutStatus: (
    e: MouseEvent,
    medicineId: string,
    medicineStandarPrice: MedicineStandardPriceVo
  ) => void;
  onClickMedicine: (isEdit?: boolean, medicineId?: string) => void;
  page: number;
  setPage: (page: number) => void;
}
const MedicineTable = ({
  medicines,
  onChangeSoldOutStatus,
  onClickMedicine,
  page,
  setPage
}: MedicineTableProps) => {
  const isEmptyMedicines = isEmpty(medicines.pharmacyHandlingMedicines?.[0]);

  const onRenderEmptyMedicine = () => {
    return (
      <EmptyMedicineWrapper>
        약품 정보를 등록하면, 재고 여부가 표시되어{'\n'}환자가 약국을 선택할
        가능성이 높아져요.
      </EmptyMedicineWrapper>
    );
  };
  return (
    <Wrapper>
      <table>
        <TableHeader>
          <tr>
            {HEADERS.map(header => (
              <Th key={`header_${header.name}`} flex={header.flex}>
                {header.name}
              </Th>
            ))}
          </tr>
        </TableHeader>
        {isEmptyMedicines ? (
          onRenderEmptyMedicine()
        ) : (
          <tbody>
            {medicines.pharmacyHandlingMedicines.map((medicine, key) => (
              <TableBodyRow
                key={key}
                isEmpty={isEmpty(medicine)}
                onClick={() => onClickMedicine(true, medicine.medicineId)}>
                <Td flex={1}>{medicine.insuranceCode}</Td>
                <Td flex={2}>{medicine.medicineName}</Td>
                <Td flex={1}>
                  {medicine.medicineStandardPrices?.map(item => (
                    <ColTd key={`unit_${item.medicineStandardId}`} flex={1}>
                      {item.medicineStandard}
                    </ColTd>
                  ))}
                </Td>
                <Td flex={1}>
                  {medicine.medicineStandardPrices?.map(item => (
                    <ColTd key={`unit_${item.medicineStandardId}`} flex={1}>
                      {formatComma(item.price)}원
                    </ColTd>
                  ))}
                </Td>
                <Td flex={1}>
                  {medicine.medicineStandardPrices?.map(item => (
                    <ColTd
                      key={`unit_${item.medicineStandardId}`}
                      flex={1}
                      onClick={e =>
                        onChangeSoldOutStatus(e, medicine.medicineId, item)
                      }>
                      <CheckBox checked={item.isSoldOut} />
                    </ColTd>
                  ))}
                </Td>
              </TableBodyRow>
            ))}
          </tbody>
        )}
      </table>

      <div>
        <PaginationComponent
          setPage={setPage}
          totalCount={medicines.totalCount || 1}
          page={page}
          itemsCountPerPage={MEDICINE_PAGE_LIMIT}
        />
      </div>
    </Wrapper>
  );
};

export default MedicineTable;

const Wrapper = styled.div`
  width: 100%;
  table {
    width: 100%;
    border-radius: 8px;
    border-collapse: collapse;

    ${({ theme }) => `
      background: ${theme.DF_COLOR.WHITE}
    `};

    tbody {
      max-height: 810px;
      display: block;
      overflow-y: scroll;
    }
  }
`;

const TableHeader = styled.thead`
  tr {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 14px 20px;

    ${({ theme }) => `
      border-bottom: 1px solid ${theme.DF_COLOR.GRAY200};
    `};
  }
`;

const Th = styled.th<{ flex: number }>`
  text-align: center;

  ${({ theme, flex }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY400};
    flex: ${flex};
  `};
`;

const TableBodyRow = styled.tr<{ isEmpty?: boolean }>`
  display: flex;
  flex-flow: row;
  width: 100%;
  align-items: center;
  cursor: pointer;
  min-height: 56px;
  padding: 0 20px;

  ${({ theme, isEmpty }) => `
    border-bottom: ${isEmpty ? 1 : 2}px solid;
    border-color: ${theme.DF_COLOR.GRAY200};
    
    &:hover {
      background: ${theme.DF_COLOR.GRAY20};
    }
  `};
  &: last-child {
    border-bottom: none;
  }
`;

const RowDataStyle = css`
  text-align: center;
  align-items: center;
  padding: 17px 0;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY900};
  `};
`;

const Td = styled.td<{ flex: number }>`
  ${RowDataStyle};

  ${({ flex }) => `
    flex: ${flex};
  `};
`;

const ColTd = styled.span<{ flex: number }>`
  ${RowDataStyle};

  display: flex;
  flex-direction: column;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  ${({ flex }) => `
    flex: ${flex};
  `}
`;

const EmptyMedicineWrapper = styled.div`
  height: 544px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  white-space: pre;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17SB};
    color: ${theme.DF_COLOR.GRAY300};
  `};
`;
